import React from 'react';
import { deliveredOrderPrompt } from '../constants/statuses';

/**
 * THE delivered-order PROMPT pill — one definition, rendered in
 * `OrderTableView.js`'s status cell (it also served the retired card rows until
 * 2026-10-18), added 2026-09-11.
 *
 * Owner's problem, verbatim: *"the buyer doesn't know WHY a delivered order is
 * still not completed"*. A row can sit at `delivered` indefinitely with nothing
 * on screen naming what is outstanding, because `status` and `completed_at` are
 * independent columns (see `isOrderComplete`'s docblock).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 A PROMPT, NEVER A GATE — THE RULING, NOT AN IMPLEMENTATION CHOICE
 * ═══════════════════════════════════════════════════════════════════════════
 * Owner, 2026-09-11: *"marking as completed can be done after delivery and/or
 * after paperwork is uploaded. Some buyers will upload paperwork others will
 * not."* Paperwork is OPTIONAL. So `No paperwork` is NOT an error state and is
 * deliberately not drawn as one — amber, the house's "worth a look" weight,
 * never red and never a warning glyph — and nothing here may ever be wired
 * into whether `Mark as Complete` is offered. Turning either pill into a
 * precondition reverses the ruling.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ♿ THE TEXT IS THE CARRIER, SO NO 1.4.11 MEASUREMENT IS OWED — STATED HERE
 *    RATHER THAN LEFT TO BE RE-DERIVED
 * ═══════════════════════════════════════════════════════════════════════════
 * WCAG 1.4.11's 3:1 minimum governs NON-TEXT carriers. Each pill's meaning is
 * spelled out in words inside it, so colour is redundant and the applicable
 * rule is 1.4.3 (text contrast), which the reused badge pattern already meets
 * everywhere else in this app.
 *
 * 🪤 This repo has TWICE promoted a redundant colour signal to a sole carrier
 * by deleting the text beside it and not noticing — `DueIndicator`'s
 * `bg-amber-500` measured 2.15:1, `OrderAttachmentMarker`'s `text-gray-400`
 * 2.54:1, both failing, both green-at-the-time. **If anyone ever shortens
 * these pills to a glyph or a bare dot, the colours must be MEASURED against
 * the real row background in both themes first.** That is the whole reason
 * this paragraph exists.
 *
 * 🪤 The classes are the existing badge pattern and carry their `dark:` pair
 * inline, per root CLAUDE.md's "Badge/pill pattern: always include both
 * variants". Neither token is in `index.css`'s global dark `!important`
 * table (checked: that table covers `bg-gray-{50,100,200}` and the `-50/-100/
 * -200` tints, i.e. `bg-gray-100` IS covered — so the grey pill's `dark:`
 * background is delivered by index.css rather than by Tailwind. It is kept
 * for declared intent, exactly as `ApprovalStatus.js`'s docblock records for
 * the same situation, and the TEXT colour is not in that table so it is live).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 THE NATIVE `title` SHADOWS NOTHING HERE — CHECKED, NOT ASSUMED
 * ═══════════════════════════════════════════════════════════════════════════
 * A native `title` on a CHILD beats its parent's, which has been a real defect
 * in this repo three times (`LeadTimeDot` in the Date cell, and both file
 * markers inside the order-number element). This pill's parent is
 * `<td data-col="status">`, which **carries no `title` of its own** — verified
 * by reading the file. The pill is also its own element with its own text, not
 * a glyph sitting inside somebody else's tooltip target.
 *
 * 🔴 If a status-cell tooltip is ever added, this `title` must move UP to it
 * rather than being duplicated — the resolution `DueIndicator`'s
 * `titleOnParent` prop and the unified Date-cell tooltip both reached.
 *
 * @param {{ order: object, testIdPrefix?: string }} props
 */
export default function DeliveredOrderPill({ order, testIdPrefix }) {
  const prompt = deliveredOrderPrompt(order);
  // 🪤 Returns null for EVERY non-prompting case, including a delivered order
  // whose `has_delivery_paperwork` is missing — see the predicate's fail-quiet
  // rule. Rendering an empty `<span>` instead would leave a stray flex gap in
  // the status cell on the majority of rows.
  if (!prompt) return null;

  const isReady = prompt === 'ready';
  const testId = `${testIdPrefix ? `${testIdPrefix}-` : ''}delivered-pill-${isReady ? 'ready' : 'no-paperwork'}`;

  return (
    <span
      data-testid={testId}
      // 🔑 The tooltip says what to DO, not what is true — the pill's own text
      // already says what is true, and a tooltip that merely restates its
      // trigger is the two-tooltips defect wearing a helpful hat.
      title={isReady
        ? 'Delivered with paperwork attached — Mark as Complete when you are done.'
        : 'Delivered but not yet marked complete, and no delivery paperwork has been uploaded. Upload the delivery note if you have one, then Mark as Complete.'}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isReady
          ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
      }`}
    >
      {isReady ? 'Ready to complete' : 'No paperwork'}
    </span>
  );
}
